<template>
	<nta-dialog
		:show="show"
		:title="title"
		height="auto"
		width="35em"
		:symbol-lang="domainStore.languagePreference"
		@close="() => cancel()"
	>
		<template #actions>
			<v-btn min-width="6em" @click="cancel()">{{
				symbolsStore.GUILabel("4c03e40c-9b2d-4090-9eca-46a4f6fd2a4e")
			}}</v-btn>
			<v-btn
				:disabled="!domainStore.hasAccessRights(UserRole.Editor) || !template || mode == 'edit'"
				color="success"
				:loading="props.loading"
				@click="props.mode == 'edit' ? save() : create()"
			>
				{{
					props.mode == "edit"
						? symbolsStore.GUILabel("6c951dfc-4d19-4d3a-b89b-b45a88d11b3f")
						: symbolsStore.GUILabel("59c65cfa-cd8d-47e2-ab59-003330041c4b", { label: "" })
				}}
			</v-btn>
		</template>
		<template #more-actions>
			<!-- <LanguageSelectorFlags :lang="lang" @select="(l: LanguageCode) => lang = l" /> -->
			<v-btn
				v-show="props.mode == 'edit'"
				:disabled="!domainStore.hasAccessRights(UserRole.Editor)"
				prepend-icon="mdi-delete"
				color="error"
				@click="remove"
			>
				{{
					symbolsStore.GUILabel("5cb8eb65-3c50-4152-9bb6-b610355cba12", {
						label: symbolsStore.GUILabel("bf45d746-d0ca-4e20-88ea-d646d99de966"),
					})
				}}
			</v-btn>
		</template>
		<template #default>
			<v-card-text>
				<v-item-group mandatory>
					<v-form ref="form" v-model="valid" lazy-validation>
						<v-autocomplete
							v-if="$props.mode === 'create'"
							v-model="template"
							:disabled="props.mode === 'edit' || isSingleDataspace"
							:items="templateWorkspacesOfTemplateDataspace"
							:item-title="(item: TemplateWorkspace) => symbolsStore.templateWorkspaceTerm(item)"
							:item-value="(item: TemplateWorkspace) => item.id"
							:label="
								symbolsStore.GUILabel('436fcee8-848b-447d-8979-cd1c98cc61c8', {
									label: symbolsStore.GUILabel('bf45d746-d0ca-4e20-88ea-d646d99de966'),
								})
							"
						>
						</v-autocomplete>

						<v-text-field
							v-model="name"
							autofocus
							:disabled="!domainStore.hasAccessRights(UserRole.Editor) || props.mode == 'edit'"
							variant="outlined"
							:label="symbolsStore.GUILabel('20bfbe7b-c76e-46a5-ab88-16d53fb68945', { label: '' })"
							@keydown.enter="props.mode == 'edit' ? save() : create()"
						></v-text-field>
						<v-textarea
							v-model="description"
							:disabled="!domainStore.hasAccessRights(UserRole.Editor) || props.mode == 'edit'"
							variant="outlined"
							:label="symbolsStore.GUILabel('ceeb13a6-6e51-427d-9e2a-12249b8512a5')"
						></v-textarea>
					</v-form>
				</v-item-group>
			</v-card-text>
		</template>
	</nta-dialog>
</template>

<script setup lang="ts">
import type { LanguageCode, TemplateDataspace, TemplateWorkspace } from "@lr-notitia/notitia-operator/schemas";
import { ref, watch, computed } from "vue";
import { useDomainStore } from "../../../store/domain";
import { useInstanceDataspacesStore } from "../../../store/i-dataspace";
import { useInstanceWorkspacesStore } from "../../../store/i-workspace";
import { useModelGraphStore } from "../../../store/modelGraph";
import { useSymbolsStore } from "../../../store/symbols";
import { uniqueNamesGenerator, adjectives, colors, animals } from "unique-names-generator";

import LanguageSelectorFlags from "../../../components/LanguageSelectorFlags.vue";
import { UserRole } from "@lr-notitia/notitia-operator/schemas";

const domainStore = useDomainStore();
const modelStore = useModelGraphStore();
const instanceDataspacesStore = useInstanceDataspacesStore();
const workspacesStore = useInstanceWorkspacesStore();
const symbolsStore = useSymbolsStore();

const form = ref();
const valid = ref(false);

const title = computed(() =>
	props.mode == "edit"
		? symbolsStore.GUILabel("a2c902fa-c61f-4bf0-98ce-e50535d4826a", {
				label: symbolsStore.GUILabel("bf45d746-d0ca-4e20-88ea-d646d99de966"),
		  })
		: symbolsStore.GUILabel("5a248943-1389-403f-8026-30d59c17ccd2", {
				label: symbolsStore.GUILabel("bf45d746-d0ca-4e20-88ea-d646d99de966"),
		  })
);
const lang = ref<LanguageCode>(domainStore.languagePreference);
const template = ref<string>("");
const name = ref("");
const description = ref("");

const props = defineProps<{
	show: boolean;
	mode: "create" | "edit";
	workspaceId?: string;
	loading: boolean;
}>();
const emits = defineEmits(["remove", "done", "save", "create"]);

const workspace = computed(() => {
	return workspacesStore.getById(props["workspaceId"] || "");
});

const templateWorkspacesOfTemplateDataspace = computed(() => {
	let templateDataspaceId = instanceDataspacesStore.openedDataspace?.template;
	if (!templateDataspaceId) return [];
	let templateDataspace = modelStore.graph.nodes[templateDataspaceId] as TemplateDataspace;
	if (!templateDataspace) return [];
	return templateDataspace.workspaces.map((id) => modelStore.graph.nodes[id] as TemplateWorkspace);
});

const isSingleDataspace = computed(() => {
	return templateWorkspacesOfTemplateDataspace.value.length === 1;
});

function create() {
	if (!domainStore.hasAccessRights(UserRole.Editor)) return;
	if (!name.value) name.value = getRndName();
	form.value.validate().then((d: any) => {
		if (!d.valid) return;
		emits("create", { template: template.value, name: name.value, description: description.value });
	});
}

function save() {
	if (!domainStore.hasAccessRights(UserRole.Editor)) return;
	if (!name.value) name.value = getRndName();
	form.value.validate().then((d: any) => {
		if (!d.valid) return;
		emits("save", workspace.value, name.value, description.value, lang.value);
	});
}

function remove() {
	if (!domainStore.hasAccessRights(UserRole.Editor)) return;
	emits("remove", props.workspaceId);
	emits("done");
}

function cancel() {
	emits("done");
}

// function that returns a random name consisting of an adjective, a color and an animal
function getRndName(): string {
	return uniqueNamesGenerator({
		dictionaries: [adjectives, colors, animals],
		style: "capital",
		separator: " ",
	});
}

watch(
	() => props.show,
	() => {
		if (!props.show) return;
		template.value = workspace.value?.template || "";
		if (isSingleDataspace.value) template.value = templateWorkspacesOfTemplateDataspace.value[0].id;
		name.value =
			workspacesStore.workspaceDescriptors[instanceDataspacesStore.openedDataspaceId ?? ""]?.[
				props.workspaceId || ""
			]?.label;
		description.value =
			workspacesStore.workspaceDescriptors[instanceDataspacesStore.openedDataspaceId ?? ""]?.[
				props.workspaceId || ""
			]?.description;
	},
	{ immediate: true }
);
</script>

<style scoped lang="scss"></style>
